import { createClient } from '@supabase/supabase-js';
import { ensureEntityFolder } from './driveRoots';
import { normalizePayer, normalizeServiceLines, CLIENT_STATUSES } from './serviceModel';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Check your .env.local file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Guardian/emergency-contact full-name split: first whitespace-separated
// token -> first name, everything after it -> last name. Shared by every
// place that still ingests an unsplit name (CSV import, lead conversion).
export const splitFullName = (full) => {
  if (!full || String(full).trim() === '' || String(full).trim() === 'nan') return { first: null, last: null };
  const parts = String(full).trim().split(/\s+/);
  return { first: parts[0], last: parts.slice(1).join(' ') || null };
};

// "Last, First" display, the app-wide convention for client/staff/lead
// names (matches AxisCare's own convention and standard healthcare/HR
// roster practice). Use wherever first/last are already available as
// separate fields.
export const lastFirst = (first, last) => {
  if (!last) return first || '';
  if (!first) return last;
  return `${last}, ${first}`;
};

// Same, but for records that only have a single full_name text field
// (profiles.full_name has no first/last split at all; staff.full_name
// predates the first_name/last_name columns added 2026-07-26). Naive
// last-token-as-surname split — good enough for display, not a source
// of truth.
export const fullNameLastFirst = (full) => {
  if (!full) return '';
  const parts = String(full).trim().split(/\s+/);
  if (parts.length < 2) return full;
  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(' ');
  return `${last}, ${rest}`;
};

export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password });

export const signOut = () => supabase.auth.signOut();

export const getClients = (filters = {}) => {
  let query = supabase.from('clients').select('*');
  if (filters.zone) query = query.eq('client_status_zone', filters.zone);
  if (filters.status) query = query.eq('current_status', filters.status);
  if (filters.county) query = query.eq('county', filters.county);
  if (filters.responsible) query = query.eq('responsible_person_name', filters.responsible);
  if (filters.serviceType) query = query.eq('service_type', filters.serviceType);
  if (filters.clientType) query = query.contains('client_type', [filters.clientType]);
  if (filters.nurseAssessment !== undefined) query = query.eq('nurse_assessment_complete', filters.nurseAssessment);
  if (filters.eligibility) query = query.eq('medicaid_eligible', filters.eligibility);
  // Apply sort — use selected column or fall back to created_at desc
  if (filters.sortBy) {
    query = query.order(filters.sortBy, { ascending: filters.sortAsc ?? true, nullsFirst: false });
  } else {
    query = query.order('created_at', { ascending: false });
  }
  return query;
};

export const getClient = (id) =>
  supabase.from('clients').select('*').eq('id', id).single();

export const createClient_ = (data) =>
  supabase.from('clients').insert([data]).select().single();

export const updateClient = (id, data) =>
  supabase.from('clients').update(data).eq('id', id).select().single();

export const updateStaff = (id, data) =>
  supabase.from('staff').update(data).eq('id', id).select().single();

// Slim client roster for the Service County Map — just enough per client to
// group by county and show a status pill; avoids pulling every clinical
// column for what's just a geographic count.
export const getClientsForCountyMap = () =>
  supabase.from('clients')
    .select('id, client_id, first_name, last_name, county, current_status')
    .order('last_name');

// ── Topbar search ────────────────────────────────────────────────────────────
// Names display as "Last, First" everywhere, so that is what people type into
// the search box — and a comma is a reserved character in a PostgREST filter.
// The old single-string ilike therefore made PostgREST return a 400 for any
// query containing one, `data` came back null, and the dropdown went empty;
// backing up to a few letters "fixed" it only because it removed the comma.
// It also matched the whole string against one column at a time, so a query
// spanning first and last name ("Justin Arroyo") could never match anything.
//
// The query is split into tokens, each token is matched against every
// searchable column (OR), and the tokens are ANDed by chaining .or() — so
// "Arroyo Justin", "Justin Arroyo" and "Arroyo Sanchez, Justin" all find the
// same client. Values are double-quoted so any remaining reserved character
// (a dot in an email, say) is literal rather than syntax.
const searchTokens = (query) =>
  String(query || '').split(/[\s,]+/).map(t => t.replace(/["\\]/g, '').trim()).filter(Boolean).slice(0, 6);
const tokenFilter = (cols, t) => cols.map(c => `${c}.ilike."%${t}%"`).join(',');
const applyTokens = (q, cols, tokens) => tokens.reduce((acc, t) => acc.or(tokenFilter(cols, t)), q);
const SEARCH_LIMIT = 8;

// The authorization numbers on the client record are the current ones; every
// PA a client has ever had is in prior_auth_history, so a number from an
// expired or superseded authorization is found there (2026-10-02 — until then
// no PA number could be searched at all).
const CLIENT_PA_COLS = ['dma_80_pa_number', 'dma_6a_number', 'dme_auth_number'];
const CLIENT_SEARCH_COLS = ['first_name', 'last_name', 'goes_by', 'client_id', 'medicaid_id', 'phone', 'guardian_phone', 'email', 'responsible_person_name', 'guardian_first_name', 'guardian_last_name', 'county', 'referral_source', 'diagnosis_summary', ...CLIENT_PA_COLS];
const CLIENT_HIT_COLS = `id, client_id, first_name, last_name, goes_by, current_status, county, ${CLIENT_PA_COLS.join(', ')}`;
const byName = (a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);

/**
 * Clients matching the query. A query that is one number of four or more
 * digits is also looked up in the PA history, and each hit carries the PA
 * number it matched on (`pa_match`) so the dropdown can say why it is there.
 */
export const searchClients = async (query) => {
  const tokens = searchTokens(query);
  const pa = tokens.length === 1 && /^\d{4,}$/.test(tokens[0]) ? tokens[0] : null;
  const [direct, history] = await Promise.all([
    applyTokens(supabase.from('clients').select(CLIENT_HIT_COLS), CLIENT_SEARCH_COLS, tokens)
      .order('last_name', { ascending: true }).order('first_name', { ascending: true }).limit(SEARCH_LIMIT),
    pa
      ? supabase.from('prior_auth_history').select('client_id, auth_number').ilike('auth_number', `%${pa}%`).limit(SEARCH_LIMIT)
      : Promise.resolve({ data: [] }),
  ]);
  if (direct.error) return direct;
  let rows = direct.data || [];
  if (!pa) return { data: rows, error: null };

  const fromHistory = new Map((history.data || []).filter(h => h.client_id).map(h => [h.client_id, h.auth_number]));
  const missing = [...fromHistory.keys()].filter(id => !rows.some(r => r.id === id));
  if (missing.length) {
    const { data: extra } = await supabase.from('clients').select(CLIENT_HIT_COLS).in('id', missing);
    rows = [...rows, ...(extra || [])].sort(byName).slice(0, SEARCH_LIMIT);
  }
  return {
    data: rows.map(r => ({
      ...r,
      pa_match: CLIENT_PA_COLS.map(c => r[c]).find(v => v && String(v).includes(pa)) || fromHistory.get(r.id) || null,
    })),
    error: null,
  };
};

// Staff half — only called for users with HR module access (gated client-side
// in Topbar.js via useModules().hasHR before this is ever invoked).
const STAFF_SEARCH_COLS = ['first_name', 'last_name', 'goes_by', 'full_name', 'employee_id', 'email', 'phone', 'position_code', 'county'];
export const searchStaff = (query) =>
  applyTokens(
    supabase.from('staff').select('id, full_name, first_name, last_name, employee_id, position_code, email, status'),
    STAFF_SEARCH_COLS, searchTokens(query),
  ).or('hidden.is.null,hidden.eq.false').order('last_name', { ascending: true }).order('first_name', { ascending: true }).limit(SEARCH_LIMIT);

const LEAD_SEARCH_COLS = ['first_name', 'last_name', 'lead_id', 'medicaid_id', 'phone', 'guardian_phone', 'email', 'county', 'referral_source'];
export const searchLeads = (query) =>
  applyTokens(
    supabase.from('leads').select('id, lead_id, first_name, last_name, current_status, county, converted_client_id'),
    LEAD_SEARCH_COLS, searchTokens(query),
  ).order('last_name', { ascending: true }).order('first_name', { ascending: true }).limit(SEARCH_LIMIT);

export const getActivityLog = (clientId) =>
  supabase.from('activity_log').select('*').eq('client_id', clientId).order('created_at', { ascending: false });

export const addActivityLog = (data) =>
  supabase.from('activity_log').insert([data]).select().single();

export const getRecentActivity = (limit = 10) =>
  supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(limit);

export const getAllActivityLogs = (limit = 500) =>
  supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(limit);

export const updateActivityLog = (id, data) =>
  supabase.from('activity_log').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select().single();

export const deleteActivityLog = (id) =>
  supabase.from('activity_log').delete().eq('id', id);

export const getTasks = (filters = {}) => {
  let query = supabase.from('tasks').select('*').order('due_date', { ascending: true });
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.assignedTo) query = query.eq('assigned_to_id', filters.assignedTo);
  if (filters.clientId) query = query.eq('client_id', filters.clientId);
  return query;
};

export const createTask = (data) =>
  supabase.from('tasks').insert([data]).select().single();

export const updateTask = (id, data) =>
  supabase.from('tasks').update(data).eq('id', id).select().single();

export const getPipelineSummary = () =>
  supabase.from('pipeline_summary').select('*');

export const getStatusOptions = () =>
  supabase.from('status_options').select('*').order('sort_order');

export const getDashboardStats = () =>
  supabase.from('dashboard_stats').select('*').single();

export const getReferralSourceStats = () =>
  supabase.from('referral_source_stats').select('*');

export const getCountyStats = () =>
  supabase.from('county_stats').select('*');

export const getMonthlyTrend = () =>
  supabase.from('monthly_referral_trend').select('*');

export const getExpiringAuthorisations = () =>
  supabase.from('clients')
    .select('id, client_id, first_name, last_name, pa_expiry_date, dma_6a_expiry_date, responsible_person_name')
    .or(`pa_expiry_date.lte.${new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]},dma_6a_expiry_date.lte.${new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]}`)
    .order('pa_expiry_date', { ascending: true });

export const getStaleClients = () =>
  supabase.from('clients')
    .select('id, client_id, first_name, last_name, current_status, last_activity_date, responsible_person_name')
    .or('last_activity_date.lt.' + new Date(Date.now() - 7*24*60*60*1000).toISOString() + ',last_activity_date.is.null')
    .not('client_status_zone', 'eq', 'Inactive')
    .order('last_activity_date', { ascending: true })
    .limit(10);

export const getEligibilityPending = () =>
  supabase.from('clients')
    .select('id, client_id, first_name, last_name, current_status, medicaid_eligible, responsible_person_name')
    .eq('medicaid_eligible', 'Pending')
    .not('client_status_zone', 'eq', 'Inactive')
    .order('created_at', { ascending: true });

export const submitIntakeForm = (data) => {
  // Helper to convert empty strings to null for database compatibility
  const toNullIfEmpty = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string' && val.trim() === '') return null;
    return val;
  };

  // Only send fields that exist in the intake_submissions table
  const allowed = {
    client_type: data.client_type || 'gapp', // Default to gapp, accepts 'gapp' or 'adult'
    first_name: toNullIfEmpty(data.first_name),
    last_name: toNullIfEmpty(data.last_name),
    date_of_birth: toNullIfEmpty(data.date_of_birth),
    gender: toNullIfEmpty(data.gender),
    language: toNullIfEmpty(data.language),
    address: toNullIfEmpty(data.address),
    city: toNullIfEmpty(data.city),
    county: toNullIfEmpty(data.county),
    state: toNullIfEmpty(data.state),
    zip: toNullIfEmpty(data.zip),
    medicaid_id: toNullIfEmpty(data.medicaid_id),
    private_insurance: data.private_insurance === true || data.private_insurance === 'Yes' ? true : (data.private_insurance === false || data.private_insurance === 'No' ? false : null),
    insurance_name: toNullIfEmpty(data.insurance_name),
    guardian_first_name: toNullIfEmpty(data.guardian_first_name),
    guardian_last_name: toNullIfEmpty(data.guardian_last_name),
    guardian_relationship: toNullIfEmpty(data.guardian_relationship),
    guardian_phone: toNullIfEmpty(data.guardian_phone),
    guardian_email: toNullIfEmpty(data.guardian_email),
    guardian_address: toNullIfEmpty(data.guardian_address),
    emergency_first_name: toNullIfEmpty(data.emergency_first_name),
    emergency_last_name: toNullIfEmpty(data.emergency_last_name),
    emergency_relationship: toNullIfEmpty(data.emergency_relationship),
    emergency_phone: toNullIfEmpty(data.emergency_phone),
    emergency_email: toNullIfEmpty(data.emergency_email),
    emergency_address: toNullIfEmpty(data.emergency_address),
    diagnosis_summary: toNullIfEmpty(data.diagnosis_summary),
    doctor_name: toNullIfEmpty(data.doctor_name),
    doctor_practice: toNullIfEmpty(data.doctor_practice),
    doctor_address: toNullIfEmpty(data.doctor_address),
    doctor_phone: toNullIfEmpty(data.doctor_phone),
    referral_source: toNullIfEmpty(data.referral_source),
    consent_texts_emails: data.consent_texts_emails === true || data.consent_texts_emails === 'Yes' ? true : (data.consent_texts_emails === false || data.consent_texts_emails === 'No' ? false : null),
    esignature: toNullIfEmpty(data.esignature),
    form_language: data.form_language || 'en',
    // DME fields (Phase 1 intake includes DME interest)
    dme_product_categories: Array.isArray(data.dme_product_categories) && data.dme_product_categories.length > 0 ? data.dme_product_categories : null,
    dme_qualifying_diagnosis: toNullIfEmpty(data.dme_qualifying_diagnosis),
    dme_prescribing_physician: toNullIfEmpty(data.dme_prescribing_physician),
    dme_physician_npi: toNullIfEmpty(data.dme_physician_npi),
    physician_id: toNullIfEmpty(data.physician_id),   // picked from the public directory list
    dme_delivery_address_same: data.dme_delivery_address_same === true ? true : (data.dme_delivery_address_same === false ? false : null),
    dme_delivery_address: toNullIfEmpty(data.dme_delivery_address),
    service_lines: Array.isArray(data.service_lines) && data.service_lines.length > 0 ? data.service_lines : ['home_health'],
    // Household pets (lib/pets.js petColumns): null means not asked.
    household_pets: Array.isArray(data.household_pets) && data.household_pets.length > 0 ? data.household_pets : null,
    household_pets_other: toNullIfEmpty(data.household_pets_other),
    household_pets_notes: toNullIfEmpty(data.household_pets_notes),
  };
  
  return supabase.from('intake_submissions').insert([allowed]);
};

export const getPendingIntakes = () =>
  supabase.from('intake_submissions').select('*').eq('processed', false).order('submitted_at', { ascending: false });

export const markIntakeProcessed = (id, clientId) =>
  supabase.from('intake_submissions').update({ processed: true, client_id_created: clientId }).eq('id', id);

export const getProfiles = () =>
  supabase.from('profiles').select('*').eq('active', true).order('full_name');

export const getProfile = (id) =>
  supabase.from('profiles').select('*').eq('id', id).single();

export const getZoneFromStatus = (status) => {
  const zones = {
    'New Referral':'Outreach','Client Contacted':'Outreach','Response Pending':'Outreach','Waitlist':'Outreach',
    'Intake Form Submitted':'Clinical Intake','Nursing Assessment Pending':'Clinical Intake','Nursing Assessment Completed':'Clinical Intake',
    'PPOT Request Pending':'Clinical Intake','PPOT Request In Progress':'Clinical Intake',
    'Submitted (DMA6A)':'Authorization','Submitted (DMA80)':'Authorization','Approved by GAPP':'Active',
    'Active (Receiving Service)':'Active',
    'On-Hold/Suspended':'Inactive','Service Suspended':'Inactive','Discharged':'Inactive','GAPP Application Denied':'Inactive','Duplicate':'Inactive','Lost':'Inactive',
  };
  return zones[status] || 'Outreach';
};

export const bulkImportClients = async (records) => {
  const results = { success: 0, errors: [] };

  const toArray = (val) => {
    if (!val || String(val).trim() === '' || String(val).trim() === 'nan') return null;
    return String(val).split(',').map(t => t.trim()).filter(t => t.length > 0);
  };

  const toBool = (val) => {
    if (!val) return false;
    return ['yes','y','true','1'].includes(String(val).toLowerCase().trim());
  };

  const toNum = (val) => {
    if (!val || String(val).trim() === '' || String(val).trim() === 'nan') return null;
    const n = parseFloat(String(val));
    return isNaN(n) ? null : n;
  };

  const toDate = (val) => {
    if (!val || String(val).trim() === '' || String(val).trim() === 'nan') return null;
    return String(val).trim();
  };

  // Fix scientific notation e.g. 1.11828E+11 -> "111828000000"
  const fixId = (val) => {
    if (!val || String(val).trim() === '' || String(val).trim() === 'nan') return null;
    try {
      const s = String(val).trim();
      if (s.toUpperCase().includes('E+') || s.toUpperCase().includes('E-')) {
        return String(Math.round(parseFloat(s)));
      }
      return s;
    } catch { return String(val).trim(); }
  };

  const toEligible = (val) => {
    if (!val || String(val).trim() === '' || String(val).trim() === 'nan') return 'Pending';
    const v = String(val).trim();
    if (['Yes','No','Pending'].includes(v)) return v;
    if (['yes','y'].includes(v.toLowerCase())) return 'Yes';
    if (['no','n'].includes(v.toLowerCase())) return 'No';
    return 'Pending';
  };

  // guardian_name/emergency_name are no longer real columns — accept them from
  // older CSV templates for backward compatibility via splitFullName().
  for (const record of records) {
    const guardianSplit  = splitFullName(record.guardian_name);
    const emergencySplit = splitFullName(record.emergency_name);
    const processed = {
      ...record,
      guardian_first_name:  record.guardian_first_name  || guardianSplit.first,
      guardian_last_name:   record.guardian_last_name   || guardianSplit.last,
      emergency_first_name: record.emergency_first_name || emergencySplit.first,
      emergency_last_name:  record.emergency_last_name  || emergencySplit.last,
      medicaid_id: fixId(record.medicaid_id),
      private_insurance: toBool(record.private_insurance),
      nurse_assessment_complete: toBool(record.nurse_assessment_complete),
      client_type: toArray(record.client_type) || ['New'],
      abilities: toArray(record.abilities),
      medical_equipment: toArray(record.medical_equipment),
      therapies: toArray(record.therapies),
      assistance_needed: toArray(record.assistance_needed),
      seizures: toBool(record.seizures),
      g_tube: toBool(record.g_tube),
      uses_oxygen: toBool(record.uses_oxygen),
      potty_trained: toBool(record.potty_trained),
      diapers: toBool(record.diapers),
      contractures: toBool(record.contractures),
      swallowing_issues: toBool(record.swallowing_issues),
      has_allergies: toBool(record.has_allergies),
      in_school: toBool(record.in_school),
      personal_nurse_in_school: toBool(record.personal_nurse_in_school),
      has_iep: toBool(record.has_iep),
      in_dfcs_custody: toBool(record.in_dfcs_custody),
      gapp_household_member: toBool(record.gapp_household_member),
      weight_lbs: toNum(record.weight_lbs),
      school_days_per_week: toNum(record.school_days_per_week),
      date_of_birth: toDate(record.date_of_birth),
      referral_date: toDate(record.referral_date),
      dma_6a_submission_date: toDate(record.dma_6a_submission_date),
      dma_6a_expiry_date: toDate(record.dma_6a_expiry_date),
      dma_80_submission_date: toDate(record.dma_80_submission_date),
      pa_expiry_date: toDate(record.pa_expiry_date),
      pa_renewal_date: toDate(record.pa_renewal_date),
      eligibility_checked_date: toDate(record.eligibility_checked_date),
      medicaid_eligible: toEligible(record.medicaid_eligible),
      current_status: String(record.current_status || '').trim() || 'New Referral',
    };
    // Retired 2026-10-20: whether a client is in the approved area is worked
    // out from their county (lib/serviceArea.js). An old spreadsheet may still
    // carry the column; it is dropped rather than sent to a column that is gone.
    delete processed.approved_county;
    // Not real columns — drop them so an older CSV template doesn't send an
    // unknown-column error (their data already landed in the split fields above).
    delete processed.guardian_name;
    delete processed.emergency_name;
    delete processed.client_status_zone; // derived from the status by the database
    if (!CLIENT_STATUSES.includes(processed.current_status)) {
      results.errors.push({ record: record.client_id, error: `Unknown status "${processed.current_status}"` });
      continue;
    }
    // An existing client's status changes the same way as everywhere else —
    // logged, payer-checked, and closing them ends their schedules — so it is
    // not overwritten by the upsert.
    const { data: existing } = record.client_id
      ? await supabase.from('clients').select('id, current_status').eq('client_id', record.client_id).maybeSingle()
      : { data: null };
    const importedStatus = processed.current_status;
    if (existing) delete processed.current_status;
    const { data, error } = await supabase.from('clients').upsert([processed], { onConflict: 'client_id' }).select('id, client_id, drive_folder_id').single();
    if (!error && existing && existing.current_status !== importedStatus) {
      const { error: stErr } = await supabase.rpc('client_change_status', { p_client_id: existing.id, p_new_status: importedStatus, p_notes: 'Changed by CSV import' });
      if (stErr) results.errors.push({ record: record.client_id, error: `Imported, but status not changed: ${stErr.message}` });
    }
    if (error) { results.errors.push({ record: record.client_id, error: error.message }); continue; }
    results.success++;
    // Sequential, not parallel — this can be hundreds of rows and each one is
    // a live Drive API round-trip; only rows that don't already have a folder
    // cached need one (skips re-imports of already-linked clients).
    if (data?.client_id && !data.drive_folder_id) {
      try {
        const folderId = await ensureEntityFolder('client', data.client_id);
        if (folderId) await updateClient(data.id, { drive_folder_id: folderId });
      } catch (e) {
        console.error('ensureEntityFolder failed during bulk import:', e);
      }
    }
  }
  return results;
};

export const updateEligibility = (clientId, data) =>
  supabase.from('clients').update({
    medicaid_eligible: data.eligible,
    eligibility_checked_date: data.checkedDate,
    eligibility_checked_by: data.checkedBy,
    eligibility_notes: data.notes,
    eligibility_recheck_date: data.recheckDate,
  }).eq('id', clientId).select().single();

export const getPendingTasks = () =>
  supabase
    .from('tasks')
    .select('*, clients(first_name, last_name, client_id)')
    .neq('status', 'Done')
    .order('due_date', { ascending: true })
    .limit(20);

export const getDocuments = (clientId) =>
  supabase.from('documents').select('*').eq('client_id', clientId).order('created_at', { ascending: false });

export const addDocument = (data) =>
  supabase.from('documents').insert([data]).select().single();

export const deleteDocument = (id) =>
  supabase.from('documents').delete().eq('id', id);

// ── LEADS ─────────────────────────────────────────────────────────────────────

export const getLeads = (filters = {}) => {
  let query = supabase.from('leads').select('*');
  if (!filters.showClosed) {
    query = query.not('current_status', 'in', '("Not Interested","Unreachable","Duplicate","Deceased","Ineligible","Moved / Outside Service Area","Chose Another Provider","Other","Converted")');
  }
  if (filters.status) query = query.eq('current_status', filters.status);
  return query.order('created_at', { ascending: false });
};

export const getLead = (id) =>
  supabase.from('leads').select('*').eq('id', id).single();

export const createLead = (data) =>
  supabase.from('leads').insert([data]).select().single();

// Creates a lead from a website contact form submission (lightweight, 6 fields)
export const createLeadFromWebsiteContact = (data) =>
  supabase.from('leads').insert([{
    first_name:      data.first_name  || null,
    last_name:       data.last_name   || null,
    email:           data.email       || null,
    phone:           data.phone       || null,
    service_type:    data.service     || null,
    service_lines:   ['home_health'],
    current_status:  'New Lead',
    referral_source: 'Website Contact Form',
    inbound_channel: 'website_contact',
    notes:           data.message     || null,
  }]).select().single();

export const updateLead = (id, data) =>
  supabase.from('leads').update(data).eq('id', id).select().single();

export const getLeadActivity = (leadId) =>
  supabase.from('activity_log').select('*').eq('lead_id', leadId).order('created_at', { ascending: false });

export const addLeadActivity = (data) =>
  supabase.from('activity_log').insert([data]).select().single();

export const getLeadTasks = (leadId) =>
  supabase.from('tasks').select('*').eq('lead_id', leadId).order('due_date', { ascending: true });

export const getActiveLeads = () =>
  supabase.from('leads')
    .select('*')
    .not('current_status', 'in', '("Not Interested","Unreachable","Duplicate","Deceased","Ineligible","Moved / Outside Service Area","Chose Another Provider","Other","Converted")')
    .order('created_at', { ascending: false });

export const convertLeadToPatient = async (leadId, performedBy) => {
  const { data: lead, error: leadError } = await supabase.from('leads').select('*').eq('id', leadId).single();
  if (leadError) return { error: leadError };

  // client_id is intentionally omitted — every client-creation path relies on
  // the database's own G-number default rather than generating one here, so
  // lead conversions get the same sequence as everyone else.
  const clientData = {
    first_name: lead.first_name,
    last_name: lead.last_name,
    phone: lead.phone,
    email: lead.email,
    date_of_birth: lead.date_of_birth,
    county: lead.county,
    referral_source: lead.referral_source,
    client_type: lead.client_type || ['New'],
    // Carried over deliberately: without these the client fell back to the
    // column defaults (gapp + home_health), so a DME lead converted into a
    // GAPP home health client with no trace of what was actually requested.
    service_type: normalizePayer(lead.service_type),
    service_lines: normalizeServiceLines(
      lead.service_lines,
      normalizeServiceLines(lead.service_type),
    ),
    guardian_first_name: splitFullName(lead.guardian_name).first,
    guardian_last_name: splitFullName(lead.guardian_name).last,
    guardian_phone: lead.guardian_phone,
    household_pets: lead.household_pets || null,
    household_pets_other: lead.household_pets_other || null,
    household_pets_notes: lead.household_pets_notes || null,
    responsible_person_id: lead.assigned_to_id,
    responsible_person_name: lead.assigned_to_name,
    current_status: 'New Referral',
    medicaid_eligible: 'Pending',
    nurse_assessment_complete: false,
    referral_date: new Date().toISOString().split('T')[0],
    last_activity_date: new Date().toISOString(),
    last_activity_note: `Converted from lead ${lead.lead_id}`,
  };

  const { data: client, error: clientError } = await supabase.from('clients').insert([clientData]).select().single();
  if (clientError) return { error: clientError };

  ensureEntityFolder('client', client.client_id).then(folderId => folderId && updateClient(client.id, { drive_folder_id: folderId }));

  // Mark lead as converted
  await supabase.from('leads').update({
    current_status: 'Converted',
    converted_client_id: client.id,
  }).eq('id', leadId);

  // Log to activity_log for the new client
  await supabase.from('activity_log').insert([{
    client_id: client.id,
    client_ref: client.client_id,
    activity_type: 'Lead Converted',
    notes: `Patient created from lead ${lead.lead_id} — ${lead.first_name} ${lead.last_name}`,
    performed_by_name: performedBy?.full_name,
    performed_by_id: performedBy?.id,
    new_status: 'New Referral',
  }]);

  return { data: { client, lead } };
};

// ── VERSIONING FOR NURSE ASSESSMENTS ─────────────────────────────────────────
export const createNurseAssessmentVersion = async (assessmentId, assessmentData, userId) => {
  try {
    // Get current version number
    const { data: currentVersion } = await supabase
      .from('nurse_assessments')
      .select('version_number')
      .eq('id', assessmentId)
      .single();
    
    const nextVersion = (currentVersion?.version_number || 0) + 1;
    const now = new Date().toISOString();
    
    // Create version record
    const { data, error } = await supabase.from('nurse_assessment_versions').insert([{
      nurse_assessment_id: assessmentId,
      version_number: nextVersion,
      version_created_at: now,
      version_created_by: userId,
      form_data: assessmentData,
    }]).select().single();
    
    if (error) throw error;
    
    // Update assessment with new version metadata
    await supabase.from('nurse_assessments')
      .update({
        version_number: nextVersion,
        version_created_at: now,
        version_created_by: userId,
      })
      .eq('id', assessmentId);
    
    return { data, error: null };
  } catch (err) {
    console.error('Version creation failed:', err);
    return { data: null, error: err };
  }
};

export const getNurseAssessmentVersions = (assessmentId) =>
  supabase.from('nurse_assessment_versions')
    .select('*')
    .eq('nurse_assessment_id', assessmentId)
    .order('version_number', { ascending: false });

export const getNurseAssessmentVersion = (assessmentId, versionNumber) =>
  supabase.from('nurse_assessment_versions')
    .select('*')
    .eq('nurse_assessment_id', assessmentId)
    .eq('version_number', versionNumber)
    .single();

// ── PRIOR AUTHORIZATION HISTORY ──────────────────────────────────────────────
// History log so editing/renewing an authorization never overwrites the prior
// one. clients.* stays the "current" cache; this is the version log.

export const getAuthHistory = (clientId, authType = null) => {
  let q = supabase.from('prior_auth_history')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (authType) q = q.eq('auth_type', authType);
  return q;
};

// Commit an authorization change: mark the prior active row of the same
// client+type as superseded, insert the new record as the active version, and
// refresh the current values on the clients row — all or nothing. `record`
// carries the snapshot fields (submitted_date is when the request went to DCH,
// effective_date the first day of care it covers); `currentColumns` is the
// matching clients-table patch, authorization columns only.
export const commitAuthChange = async ({ clientId, clientRef, authType, record, currentColumns, profile, cycleId = null }) => {
  // The number in force before this change — read first, because step 1
  // supersedes it. Step 4 needs it to tell a new authorization from a change
  // to the same one.
  const { data: prior } = await supabase.from('prior_auth_history').select('auth_number')
    .eq('client_id', clientId).eq('auth_type', authType).eq('status', 'active')
    .order('created_at', { ascending: false }).limit(1).maybeSingle();

  // 1–3, in one transaction (commit_auth_change): supersede the previous
  // active version of this type, insert the new one, and update the cache on
  // the client. These used to be three separate writes, and a failure between
  // them left a client with no active authorization, or a cache that no longer
  // matched its log.
  const { error: commitErr } = await supabase.rpc('commit_auth_change', {
    p_client_id: clientId,
    p_client_ref: clientRef || null,
    p_auth_type: authType,
    p_record: record || {},
    p_current: currentColumns || {},
    p_actor_id: profile?.id || null,
    p_actor_name: profile?.full_name || null,
  });
  if (commitErr) return { error: commitErr };

  // 4. An authorization answers the submission that was waiting on DCH.
  // Recording it from the Prior Auth page or the client form used to leave
  // that cycle at 'submitted' forever, so openCycle() kept returning it.
  //
  // The GAPP Submissions card says which cycle it is deciding (cycleId) and
  // closes it itself. Anywhere else, close a submitted cycle only when this
  // PA plainly answers it: GAMMIS keeps the request id as the PA number, so
  // the number matches the cycle's submission reference, or it is a new number
  // altogether. The same number with new hours is a change to the PA in force
  // — a reconsideration's outcome, or a correction — and closes nothing; that
  // used to mark whichever renewal was waiting as approved. A reconsideration
  // is never closed from here: it is decided on its own card.
  //
  // A new number with no matching reference closes a submission only when it
  // is the one waiting and carries no reference of its own to contradict it.
  // With a transfer and a renewal both waiting (Pahel Shah, G079, 2026-10-05),
  // or a reference that says otherwise, recording a PA — say the one a child
  // transfers in with — must not mark a submission approved by guessing.
  if (authType === 'PA' && record?.auth_number && !cycleId) {
    const number = String(record.auth_number).trim();
    const isNew = !prior?.auth_number || String(prior.auth_number).trim() !== number;
    const [{ data: waiting }, { data: hist }] = await Promise.all([
      supabase.from('pa_cycles').select('id, submission_reference').eq('client_id', clientId).eq('status', 'submitted')
        .neq('kind', 'reconsideration').order('created_at', { ascending: false }),
      supabase.from('prior_auth_history').select('id').eq('client_id', clientId).eq('auth_type', 'PA').eq('status', 'active')
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
    ]);
    const only = (waiting || []).length === 1 ? waiting[0] : null;
    const cycle = (waiting || []).find((c) => c.submission_reference && String(c.submission_reference).trim() === number)
      || (isNew && only && !String(only.submission_reference || '').trim() ? only : null);
    // An approval closed from here still has DCH's Letter of Notification to
    // follow (lon_followup): without it the submission dropped into Past with
    // no letter step, as Amaya Roblero Lopez's transfer (G112) did on
    // 2026-10-05. The letter date and the family email are then done from
    // the submission's letter step.
    if (cycle) {
      await supabase.from('pa_cycles').update({
        status: 'approved', decision_date: new Date().toISOString().slice(0, 10),
        auth_history_id: hist?.id || null, lon_followup: true, updated_at: new Date().toISOString(),
      }).eq('id', cycle.id);
    }
  }
  return { error: null };
};

// ── VERSIONING FOR SUPERVISOR VISITS ─────────────────────────────────────────
export const createSupervisorVisitVersion = async (visitId, visitData, userId) => {
  try {
    // Get current version number
    const { data: currentVersion } = await supabase
      .from('supervisor_visits')
      .select('version_number')
      .eq('id', visitId)
      .single();
    
    const nextVersion = (currentVersion?.version_number || 0) + 1;
    const now = new Date().toISOString();
    
    // Create version record
    const { data, error } = await supabase.from('supervisor_visit_versions').insert([{
      supervisor_visit_id: visitId,
      version_number: nextVersion,
      version_created_at: now,
      version_created_by: userId,
      form_data: visitData,
    }]).select().single();
    
    if (error) throw error;
    
    // Update visit with new version metadata
    await supabase.from('supervisor_visits')
      .update({
        version_number: nextVersion,
        version_created_at: now,
        version_created_by: userId,
      })
      .eq('id', visitId);
    
    return { data, error: null };
  } catch (err) {
    console.error('Visit version creation failed:', err);
    return { data: null, error: err };
  }
};

export const getSupervisorVisitVersions = (visitId) =>
  supabase.from('supervisor_visit_versions')
    .select('*')
    .eq('supervisor_visit_id', visitId)
    .order('version_number', { ascending: false });

export const getSupervisorVisitVersion = (visitId, versionNumber) =>
  supabase.from('supervisor_visit_versions')
    .select('*')
    .eq('supervisor_visit_id', visitId)
    .eq('version_number', versionNumber)
    .single();

// ── Projections Tool — saved scenarios ─────────────────────────────────────
// A scenario snapshots the editable state of the Finance Projections Tool
// (rows + assumptions) so it survives a reload instead of resetting to live data.

export const getProjectionScenarios = () =>
  supabase.from('projection_scenarios')
    .select('id, name, updated_at, created_by_name')
    .order('updated_at', { ascending: false });

export const getProjectionScenario = (id) =>
  supabase.from('projection_scenarios')
    .select('*')
    .eq('id', id)
    .single();

export const saveProjectionScenario = ({ id, name, payload, profile }) => {
  if (id) {
    return supabase.from('projection_scenarios')
      .update({ name, payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  }
  return supabase.from('projection_scenarios')
    .insert([{
      name,
      payload,
      created_by_id: profile?.id || null,
      created_by_name: profile?.full_name || null,
    }])
    .select()
    .single();
};

export const deleteProjectionScenario = (id) =>
  supabase.from('projection_scenarios').delete().eq('id', id);
